import React, { useState } from 'react';
import { X, AlertTriangle, FileSignature, Gavel } from 'lucide-react';
import { computeSeveranceCost } from '../../store/helpers/severance';
import { useTranslation } from '../../i18n';

/**
 * Confirmation modal for ending a driver contract.
 *
 * 02/06/2026 — Replaces the silent direct-fire path. Shows the player a
 * realistic breakdown of the severance cost (indemnité, préavis, congés
 * payés, charges) and lets them pick between **Rupture conventionnelle**
 * (default, no notice period, lower cost) and **Licenciement** (justified
 * cause, notice period due, higher total).
 *
 * If seniority < 8 months we lock to "Période d'essai" mode (cheap admin
 * cost only) since the legal indemnity isn't due.
 */
export default function FireConfirmModal({ driver, totalDays, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('rupture');
  const result = computeSeveranceCost(driver, totalDays, mode);
  const isProbation = result.mode === 'probation';

  const eur = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid={`fire-confirm-modal-${driver.id}`}
    >
      <div
        className="bg-ink-800 border border-danger/40 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 space-y-4 shadow-2xl modal-scroll"
        onClick={(e) => e.stopPropagation()}
        data-testid={`fire-confirm-modal-body-${driver.id}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-danger font-mono font-bold flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              {t('fire_modal_kicker')}
            </p>
            <h2 className="font-display text-lg font-black text-foreground mt-0.5">{driver.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('fire_modal_seniority', { months: result.seniorityMonths.toFixed(1) })} ·{' '}
              {t('fire_modal_gross', { amount: eur(result.monthlyGross) })}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1 text-slate-500 hover:text-foreground"
            data-testid="fire-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode selector */}
        {!isProbation && (
          <div className="grid grid-cols-2 gap-2" data-testid="fire-mode-selector">
            <button
              onClick={() => setMode('rupture')}
              className={`px-3 py-2.5 rounded-lg border text-left transition-colors ${
                mode === 'rupture'
                  ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
                  : 'border-ink-600 text-slate-400 hover:bg-ink-700/40'
              }`}
              data-testid="fire-mode-rupture"
            >
              <div className="flex items-center gap-1.5 text-xs font-bold mb-0.5">
                <FileSignature className="w-3.5 h-3.5" />
                {t('fire_modal_mode_rupture_title')}
              </div>
              <p className="text-[10px] text-slate-500 leading-snug">
                {t('fire_modal_mode_rupture_desc')}
              </p>
            </button>
            <button
              onClick={() => setMode('fault')}
              className={`px-3 py-2.5 rounded-lg border text-left transition-colors ${
                mode === 'fault'
                  ? 'border-rose-500/60 bg-rose-500/10 text-rose-200'
                  : 'border-ink-600 text-slate-400 hover:bg-ink-700/40'
              }`}
              data-testid="fire-mode-fault"
            >
              <div className="flex items-center gap-1.5 text-xs font-bold mb-0.5">
                <Gavel className="w-3.5 h-3.5" />
                {t('fire_modal_mode_fault_title')}
              </div>
              <p className="text-[10px] text-slate-500 leading-snug">
                {t('fire_modal_mode_fault_desc')}
              </p>
            </button>
          </div>
        )}

        {isProbation && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200">
            {t('fire_modal_probation_note')}
          </div>
        )}

        {/* Breakdown */}
        <div className="rounded-lg border border-ink-600 bg-ink-700/30 divide-y divide-ink-600/50" data-testid="severance-breakdown">
          <BreakdownRow label={t('fire_modal_row_indemnity')} value={eur(result.breakdown.indemnity)} testid="severance-indemnity" />
          <BreakdownRow label={t('fire_modal_row_notice')} value={eur(result.breakdown.notice)} testid="severance-notice" />
          <BreakdownRow label={t('fire_modal_row_paidleave')} value={eur(result.breakdown.paidLeave)} testid="severance-paidleave" />
          <BreakdownRow label={t('fire_modal_row_charges')} value={eur(result.breakdown.charges)} testid="severance-charges" />
          <div className="flex items-center justify-between px-3 py-2.5 bg-danger/10">
            <span className="text-xs font-bold text-foreground uppercase">{t('fire_modal_row_total')}</span>
            <span className="text-sm font-mono font-black text-danger" data-testid="severance-total">{eur(result.total)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold border border-ink-600 text-slate-400 hover:bg-ink-700/40"
            data-testid="fire-cancel"
          >
            {t('fire_modal_cancel')}
          </button>
          <button
            onClick={() => onConfirm({ mode })}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-danger text-white hover:bg-rose-700 transition-colors"
            data-testid="fire-confirm"
          >
            {t('fire_modal_confirm', { amount: eur(result.total) })}
          </button>
        </div>
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between px-3 py-2" data-testid={testid}>
      <span className="text-xs text-slate-300">{label}</span>
      <span className="text-xs font-mono text-foreground">{value}</span>
    </div>
  );
}
